import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, XCircle, Clock, AlertCircle, TrendingUp, MessageSquare, FileText, Rocket, ExternalLink, BookOpen } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CompanyDashboard = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [company, setCompany] = useState(null);
  const [applications, setApplications] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (companyId) {
      fetchCompanyData();
    }
  }, [companyId]);

  const fetchCompanyData = async () => {
    try {
      const [companyRes, appsRes, evalsRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}`),
        axios.get(`${API}/applications/company/${companyId}`),
        axios.get(`${API}/evaluations/company/${companyId}`)
      ]);
      setCompany(companyRes.data);
      setApplications(appsRes.data);
      setEvaluations(evalsRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching company data:", error);
      toast({
        title: "Error",
        description: "Failed to load company data",
        variant: "destructive"
      });
      setLoading(false);
    }
  };

  const handleEvaluate = async (applicationId) => {
    try {
      toast({
        title: "Evaluating...",
        description: "Nova is analyzing your application",
      });
      await axios.post(`${API}/evaluations/evaluate/${applicationId}`);
      toast({
        title: "Evaluation Complete!",
        description: "Check your results below",
      });
      fetchCompanyData();
    } catch (error) {
      console.error("Error evaluating:", error);
      toast({
        title: "Error",
        description: "Failed to evaluate application",
        variant: "destructive"
      });
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "accepted":
        return <CheckCircle2 className="h-5 w-5 text-green-600" />;
      case "rejected":
        return <XCircle className="h-5 w-5 text-red-600" />;
      case "pending":
        return <Clock className="h-5 w-5 text-yellow-600" />;
      default:
        return <AlertCircle className="h-5 w-5 text-gray-600" />;
    }
  };

  const getVerdictColor = (verdict) => {
    switch (verdict) {
      case "strong_yes":
        return "bg-green-600";
      case "yes":
        return "bg-green-500";
      case "maybe":
        return "bg-yellow-500";
      case "no":
        return "bg-red-500";
      default:
        return "bg-gray-500";
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-white flex items-center justify-center">
        <Card data-testid="company-not-found-card">
          <CardHeader>
            <CardTitle>Company Not Found</CardTitle>
            <CardDescription>The company you're looking for doesn't exist.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate("/")} data-testid="go-home-btn">Go Home</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      {/* Header */}
      <header className="border-b bg-white/70 backdrop-blur-xl shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Button variant="ghost" onClick={() => navigate("/")} data-testid="back-home-btn">
            &larr; Home
          </Button>
          <div className="flex gap-4">
            <Button variant="outline" onClick={() => navigate(`/chat/${companyId}`)} data-testid="chat-with-nova-btn">
              <MessageSquare className="mr-2 h-4 w-4" />
              Chat with Nova
            </Button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Company Info */}
        <Card className="mb-6" data-testid="company-info-card">
          <CardHeader>
            <div className="flex justify-between items-start">
              <div>
                <CardTitle className="text-3xl">{company.name}</CardTitle>
                <CardDescription className="text-lg mt-2">
                  Founder: {company.founder_name} | {company.founder_email}
                </CardDescription>
                {company.website && (
                  <a href={company.website} target="_blank" rel="noopener noreferrer" className="text-green-600 hover:underline">
                    {company.website}
                  </a>
                )}
              </div>
              <div className="flex items-center gap-2">
                {getStatusIcon(company.status)}
                <Badge className={company.status === "accepted" ? "bg-green-600" : "bg-yellow-600"} data-testid="company-status-badge">
                  {company.status}
                </Badge>
              </div>
            </div>
          </CardHeader>
        </Card>

        {/* Welcome Journey Card - Shown to all founders */}
        <Card className="mb-6 border-2 border-green-500 bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 shadow-xl overflow-hidden" data-testid="journey-welcome-card">
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-200/30 rounded-full -translate-y-16 translate-x-16"></div>
          <CardHeader className="pb-2">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-3 rounded-xl shadow-lg">
                <Rocket className="h-7 w-7 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl text-green-800">Welcome to SKI AXIS!</CardTitle>
                <CardDescription className="text-green-700 text-base">Your journey to scale begins now</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-white/70 backdrop-blur rounded-xl p-5 border border-green-200">
              <div className="flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <BookOpen className="h-5 w-5 text-green-600" />
                    <h4 className="font-semibold text-gray-800 text-lg">Your SKI AXIS Journey Guide</h4>
                  </div>
                  <p className="text-gray-600">
                    Congratulations on taking the first step! Access your comprehensive founder guide to understand the SKI AXIS program, 
                    governance frameworks, and how to maximize your accelerator experience.
                  </p>
                </div>
                <a 
                  href="https://docs.google.com/document/d/1Rafj8jpO5x0UetqekZ-7ueB7h8pUR-xXzci-mLWxCrU/edit?usp=sharing" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-300 hover:scale-105 shadow-lg whitespace-nowrap"
                  data-testid="journey-guide-link"
                >
                  <span>Start Your Journey</span>
                  <ExternalLink className="h-5 w-5" />
                </a>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="applications" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="applications" data-testid="applications-tab">Applications</TabsTrigger>
            <TabsTrigger value="evaluations" data-testid="evaluations-tab">Evaluations</TabsTrigger>
          </TabsList>

          {/* Applications Tab */}
          <TabsContent value="applications" className="space-y-4">
            {applications.length === 0 ? (
              <Card data-testid="no-applications-card">
                <CardContent className="pt-6">
                  <p className="text-center text-gray-600">No applications yet.</p>
                </CardContent>
              </Card>
            ) : (
              applications.map((app) => {
                const evaluation = evaluations.find(e => e.application_id === app.id);
                return (
                  <Card key={app.id} data-testid={`application-card-${app.id}`}>
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <CardTitle>{app.idea_description}</CardTitle>
                          <CardDescription className="mt-2">
                            <div className="space-y-1">
                              <p><strong>Problem:</strong> {app.problem_statement}</p>
                              <p><strong>Solution:</strong> {app.solution}</p>
                              <p><strong>Market:</strong> {app.target_market}</p>
                              <p><strong>Team:</strong> {app.team_size} people | <strong>Stage:</strong> {app.funding_stage}</p>
                              {app.traction && <p><strong>Traction:</strong> {app.traction}</p>}
                            </div>
                          </CardDescription>
                        </div>
                        <Badge className={app.status === "evaluated" ? "bg-green-600" : "bg-yellow-600"} data-testid={`application-status-${app.id}`}>
                          {app.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      {app.status !== "evaluated" ? (
                        <Button
                          onClick={() => handleEvaluate(app.id)}
                          className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 shadow-lg hover:scale-105 transition-all duration-300"
                          data-testid={`evaluate-btn-${app.id}`}
                        >
                          Get Nova Evaluation
                        </Button>
                      ) : evaluation ? (
                        <div className="bg-green-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            <TrendingUp className="h-5 w-5 text-green-600" />
                            <span className="font-semibold">Evaluated by Nova</span>
                          </div>
                          <p className="text-sm text-gray-600">Score: {evaluation.score}/100</p>
                          <Button
                            variant="outline"
                            size="sm"
                            className="mt-2"
                            onClick={() => document.getElementById(`eval-${app.id}`)?.scrollIntoView({ behavior: 'smooth' })}
                            data-testid={`view-evaluation-${app.id}`}
                          >
                            View Full Evaluation
                          </Button>
                        </div>
                      ) : null}
                    </CardContent>
                  </Card>
                );
              })
            )}
          </TabsContent>

          {/* Evaluations Tab */}
          <TabsContent value="evaluations" className="space-y-4">
            {evaluations.length === 0 ? (
              <Card data-testid="no-evaluations-card">
                <CardContent className="pt-6">
                  <p className="text-center text-gray-600">No evaluations yet. Submit an application first!</p>
                </CardContent>
              </Card>
            ) : (
              evaluations.map((evaluation) => (
                <Card key={evaluation.id} id={`eval-${evaluation.application_id}`} className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-white/90 backdrop-blur" data-testid={`evaluation-card-${evaluation.id}`}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <CardTitle className="text-2xl">Nova's Evaluation</CardTitle>
                      <div className="text-right">
                        <div className="text-3xl font-bold text-green-600" data-testid={`evaluation-score-${evaluation.id}`}>{evaluation.score}/100</div>
                        <Badge className={getVerdictColor(evaluation.verdict)} data-testid={`evaluation-verdict-${evaluation.id}`}>
                          {evaluation.verdict.replace("_", " ").toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Progress value={evaluation.score} className="h-2" />
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-2 flex items-center gap-2">
                        <FileText className="h-4 w-4" />
                        Feedback
                      </h4>
                      <p className="text-gray-700" data-testid={`evaluation-feedback-${evaluation.id}`}>{evaluation.feedback}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-green-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 text-green-900 flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4" />
                          Strengths
                        </h4>
                        <ul className="list-disc list-inside space-y-1">
                          {evaluation.strengths.map((strength, idx) => (
                            <li key={idx} className="text-sm text-green-800" data-testid={`strength-${evaluation.id}-${idx}`}>{strength}</li>
                          ))}
                        </ul>
                      </div>

                      <div className="bg-red-50 p-4 rounded-lg">
                        <h4 className="font-semibold mb-2 text-red-900 flex items-center gap-2">
                          <XCircle className="h-4 w-4" />
                          Areas to Improve
                        </h4>
                        <ul className="list-disc list-inside space-y-1">
                          {evaluation.weaknesses.map((weakness, idx) => (
                            <li key={idx} className="text-sm text-red-800" data-testid={`weakness-${evaluation.id}-${idx}`}>{weakness}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                      <h4 className="font-semibold mb-2 text-blue-900 flex items-center gap-2">
                        <TrendingUp className="h-4 w-4" />
                        Recommendations
                      </h4>
                      <ul className="list-disc list-inside space-y-1">
                        {evaluation.recommendations.map((rec, idx) => (
                          <li key={idx} className="text-sm text-blue-800" data-testid={`recommendation-${evaluation.id}-${idx}`}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CompanyDashboard;